import { action, atom } from '@reatom/framework'

import { Data } from '@blade/std-lib'
import { TxHash } from '@root/entities/transaction'

import type { AggregatedPositionModel } from '@root/entities/position'
import type { BulkBuildSignAndSendSwapTransactionResponseDto } from '@root/features/secure-storage/secure-storage-service'
import type {
  MultipleSwapsResult,
  MultipleSwapTransaction,
} from '@root/features/trade/swap'
import type { Error } from '@root/shared'
import type { ApiError } from '@root/shared/api'

class StageSending extends Data.TaggedClass('StageSending')<{}> {}

class StageSent extends Data.TaggedClass('StageSent')<{
  txs: Array<MultipleSwapTransaction>
}> {}
class StageFailed extends Data.TaggedClass('StageFailed')<{
  txs: Array<MultipleSwapTransaction>
  error: Error.UnknownError | ApiError.ApiError | undefined
}> {}
class StageSettled extends Data.TaggedClass('StageSettled')<{
  txs: Array<MultipleSwapTransaction>
}> {}

type Stage = StageSending | StageSent | StageSettled | StageFailed

export class SellEntirePositionModel {
  public readonly stageAtom = atom<Stage>(new StageSending())

  constructor(public readonly position: AggregatedPositionModel) {}

  public handleSignAndSendSwapData = action(
    async (
      ctx,
      data: Array<BulkBuildSignAndSendSwapTransactionResponseDto>,
    ) => {
      this.stageAtom(
        ctx,
        new StageSettled({
          txs: data.map((it) => ({
            tx: TxHash(it.signature ?? ''),
            status: it.error ? 'failed' : 'finished',
            walletId: it.walletId,
          })),
        }),
      )
    },
  )

  public handleSendSwapData = action((ctx, data: MultipleSwapsResult) => {
    const walletPositions = ctx.get(this.position.walletPositions)
    const allSettled = walletPositions.every((it) => {
      const tx = data.txs.find((tx) => tx.walletId === it.walletId)
      if (!tx) {
        return false
      }
      return tx.status !== 'active'
    })

    if (allSettled) {
      this.stageAtom(ctx, new StageSettled({ txs: data.txs }))
      return
    }

    const allReady = walletPositions.every((it) => {
      const tx = data.txs.find((tx) => tx.walletId === it.walletId)
      if (!tx) {
        return false
      }
      return tx.status === 'finished'
    })

    if (allReady) {
      this.stageAtom(ctx, new StageSettled({ txs: data.txs }))
      return
    }
    this.stageAtom(ctx, new StageSent({ txs: data.txs }))
  })

  public handleError = action(
    (ctx, error: Error.UnknownError | ApiError.ApiError) => {
      this.stageAtom(
        ctx,
        new StageFailed({
          txs: [],
          error,
        }),
      )
    },
  )

  static StageSending = StageSending
  static StageSent = StageSent
  static StageFailed = StageFailed
  static StageSettled = StageSettled
}
